import {
  RULES,
  chooseAiPlan,
  createOpenSupplyGame,
  validatePlan,
  type GameState,
  type PlayerPlan,
} from "../core/index.js";
import { getCard, getDevotion } from "../content/index.js";
import type {
  ScenarioDefinition,
  ScenarioResultKey,
  ScenarioTutorialRule,
} from "./types.js";

export function createScenarioGame(scenario: ScenarioDefinition): GameState {
  const battle = scenario.battle;
  const state = createOpenSupplyGame({
    seed: battle.seed,
    stageId: battle.stageId,
    playerNames: battle.playerNames,
    servantIds: battle.servantIds,
    openingObjectCardId: null,
  });
  state.log = [scenario.startLog];
  return state;
}

export function getScenarioTutorialRule(
  scenario: ScenarioDefinition,
  turn: number,
): ScenarioTutorialRule | null {
  return scenario.battle.tutorialRules[turn] ?? null;
}

export function validateScenarioPlan(
  state: GameState,
  scenario: ScenarioDefinition,
  plan: PlayerPlan,
  isPass: boolean,
): { ok: true } | { ok: false; reason: string } {
  const baseValidation = validatePlan(state, 0, plan);
  if (!baseValidation.ok) return baseValidation;
  if (!isPass && !plan.supplyCardId && !plan.devotionId) {
    return {
      ok: false,
      reason: "カードか献身を選ぶか、「何も置かない」を選んでください。",
    };
  }

  const rule = getScenarioTutorialRule(scenario, state.turn);
  if (!rule) return { ok: true };
  if (isPass && rule.allowPass === false) {
    return { ok: false, reason: rule.prompt };
  }
  if (rule.requiredFocusLane && plan.focusLane !== rule.requiredFocusLane) {
    return {
      ok: false,
      reason: "この時間はレーン" + rule.requiredFocusLane + "へ注目します。",
    };
  }
  if (rule.requiredCardId) {
    if (plan.supplyCardId !== rule.requiredCardId) {
      return {
        ok: false,
        reason: "《" + (getCard(rule.requiredCardId)?.name ?? "指定のカード") + "》を選んでください。",
      };
    }
    if (rule.requiredLane && plan.target.lane !== rule.requiredLane) {
      return {
        ok: false,
        reason: "《" + (getCard(rule.requiredCardId)?.name ?? "夢")
          + "》をレーン" + rule.requiredLane + "へ置いてください。",
      };
    }
  }
  if (rule.requiredDevotionId && plan.devotionId !== rule.requiredDevotionId) {
    return {
      ok: false,
      reason: "献身《" + (getDevotion(rule.requiredDevotionId)?.name ?? "指定の献身")
        + "》を選んでください。",
    };
  }
  if (rule.requiredTargetCardId) {
    const target = state.board.find((object) => object.id === plan.target.objectId);
    if (target?.kind !== rule.requiredTargetCardId) {
      return {
        ok: false,
        reason: "《" + (getCard(rule.requiredTargetCardId)?.name ?? "指定の夢")
          + "》を盤面から選んでください。",
      };
    }
  }
  return { ok: true };
}

export function buildScenarioOpponentPlan(
  state: GameState,
  scenario: ScenarioDefinition,
): PlayerPlan {
  const scripted = scenario.battle.opponentPlans[state.turn];
  if (!scripted) return chooseAiPlan(state, 1);
  if (scripted.pass) return passAt(scripted.focusLane);
  if (scripted.devotionId) {
    return {
      focusLane: scripted.focusLane,
      cardInstanceId: "",
      devotionId: scripted.devotionId,
      target: { ...scripted.target },
    };
  }

  const plan: PlayerPlan = {
    focusLane: scripted.focusLane,
    cardInstanceId: "",
    supplyCardId: scripted.cardId,
    devotionId: "",
    target: { ...scripted.target },
  };
  return validatePlan(state, 1, plan).ok ? plan : chooseAiPlan(state, 1);
}

export function getScenarioResultKey(state: GameState): ScenarioResultKey {
  const happiness = state.players[0].happiness;
  if (happiness <= RULES.nightmareLine) return "nightmare";
  if (happiness >= RULES.oversleepLine) return "oversleep";
  if (happiness >= 13 && happiness <= RULES.idealHappiness) return "ideal";
  return "low";
}

function passAt(focusLane: 1 | 2 | 3): PlayerPlan {
  return {
    focusLane,
    cardInstanceId: "",
    devotionId: "",
    target: {},
  };
}
